import { z } from "zod";

/** How many failure actions one agent version may configure. */
export const AGENT_FAILURE_ACTIONS_MAX = 5;

/** Twilio's maximum length for one outbound message body. */
export const AGENT_FAILURE_SMS_MAX_LENGTH = 1600;

/**
 * E.164: leading +, country code 1–9, then 6–14 more digits (15 digits total).
 */
export const E164_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

const workflowActionSchema = z.object({
  type: z.literal("workflow"),
  workflowId: z.uuid(),
});

const smsActionSchema = z.object({
  type: z.literal("sms"),
  fromNumberId: z.uuid(),
  to: z.string().regex(E164_PHONE_PATTERN),
  message: z.string().trim().min(1).max(AGENT_FAILURE_SMS_MAX_LENGTH),
});

/** An action that is complete enough to run when an agent fails. */
export const AgentFailureActionSchema = z.discriminatedUnion("type", [
  workflowActionSchema,
  smsActionSchema,
]);

export type AgentFailureAction = z.infer<typeof AgentFailureActionSchema>;

/**
 * What the editor stores, including rows the user has not finished. Incomplete
 * rows are kept so a save mid-edit is not wiped, and ignored at dispatch.
 */
export const AgentFailureActionDraftSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("workflow"),
    workflowId: z.string(),
  }),
  z.object({
    type: z.literal("sms"),
    fromNumberId: z.string().default(""),
    to: z.string(),
    message: z.string().max(AGENT_FAILURE_SMS_MAX_LENGTH),
  }),
]);

export type AgentFailureActionDraft = z.infer<
  typeof AgentFailureActionDraftSchema
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function configRecord(config: unknown): Record<string, unknown> {
  return isRecord(config) ? config : {};
}

/** Draft rows for the editor. Unknown shapes are dropped rather than thrown. */
export function failureActionsFromConfig(
  config: unknown,
): AgentFailureActionDraft[] {
  const raw = configRecord(config).failureActions;
  if (!Array.isArray(raw)) return [];

  const drafts: AgentFailureActionDraft[] = [];
  for (const item of raw) {
    const parsed = AgentFailureActionDraftSchema.safeParse(item);
    if (parsed.success) drafts.push(parsed.data);
    if (drafts.length >= AGENT_FAILURE_ACTIONS_MAX) break;
  }
  return drafts;
}

/** Actions that will actually run. Incomplete drafts are skipped. */
export function executableFailureActions(
  config: unknown,
): AgentFailureAction[] {
  const raw = configRecord(config).failureActions;
  if (!Array.isArray(raw)) return [];

  const actions: AgentFailureAction[] = [];
  for (const item of raw) {
    const parsed = AgentFailureActionSchema.safeParse(item);
    if (parsed.success) actions.push(parsed.data);
    if (actions.length >= AGENT_FAILURE_ACTIONS_MAX) break;
  }
  return actions;
}

/** Write the editor rows back onto an agent config object. */
export function configWithFailureActions(
  config: Record<string, unknown>,
  actions: AgentFailureActionDraft[],
): Record<string, unknown> {
  const next = { ...config };
  if (actions.length === 0) {
    delete next.failureActions;
    return next;
  }
  next.failureActions = actions.slice(0, AGENT_FAILURE_ACTIONS_MAX);
  return next;
}

export interface FailureSmsContext {
  agentName: string;
  error: string;
  runId: string;
}

const FAILURE_SMS_TOKEN = /\{(agentName|error|runId)\}/g;

/** Fill `{agentName}`, `{error}`, and `{runId}` and cap the body at Twilio's limit. */
export function renderFailureSmsMessage(
  template: string,
  context: FailureSmsContext,
): string {
  const rendered = template.replace(FAILURE_SMS_TOKEN, (match, key: string) => {
    if (key === "agentName") return context.agentName;
    if (key === "error") return context.error;
    if (key === "runId") return context.runId;
    return match;
  });
  return rendered.slice(0, AGENT_FAILURE_SMS_MAX_LENGTH);
}
